<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import { isValidVariableKey } from './variableHelpers';

const props = defineProps({
  // Each entry is { key, writtenIn: [{ id, name, typeLabel }], readIn: [...] }
  variables: {
    type: Array,
    required: true,
    validator: value => value.every(variable => 'key' in variable),
  },
});

const emit = defineEmits(['close', 'create', 'rename', 'delete']);

const { t } = useI18n();

const search = ref('');

const visibleVariables = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return props.variables;
  return props.variables.filter(variable =>
    variable.key.toLowerCase().includes(query)
  );
});

const exists = key => props.variables.some(variable => variable.key === key);

const errorFor = (key, originalKey) => {
  if (!key) return '';
  if (key === originalKey) return '';
  if (exists(key)) return t('FLOWS.EDITOR.VARIABLES.DUPLICATE_NAME');
  if (!isValidVariableKey(key)) return t('FLOWS.EDITOR.VARIABLES.INVALID_NAME');
  return '';
};

const typedKey = computed(() => search.value.trim());
const createError = computed(() => errorFor(typedKey.value));
const canCreate = computed(() => typedKey.value && !createError.value);

const create = () => {
  if (!canCreate.value) return;
  emit('create', typedKey.value);
  search.value = '';
};

// Only one row is ever renamed at a time, so a single { key, draft } beats a draft per row.
const editing = ref(null);

const editingError = computed(() =>
  editing.value ? errorFor(editing.value.draft.trim(), editing.value.key) : ''
);

const startEdit = variable => {
  editing.value = { key: variable.key, draft: variable.key };
};

const cancelEdit = event => {
  editing.value = null;
  event.target.blur();
};

// Commit runs on blur, so a name that never became valid quietly reverts rather than trapping
// focus in the row - the error under the field already said why while it was being typed.
const commitEdit = () => {
  if (!editing.value) return;
  const { key, draft } = editing.value;
  const next = draft.trim();
  editing.value = null;
  if (!next || next === key || errorFor(next, key)) return;
  emit('rename', { from: key, to: next });
};

const displayValue = variable =>
  editing.value?.key === variable.key ? editing.value.draft : variable.key;

const usageCount = variable =>
  new Set([...variable.writtenIn, ...variable.readIn].map(node => node.id))
    .size;

const confirmingKey = ref(null);

const requestDelete = variable => {
  if (usageCount(variable)) confirmingKey.value = variable.key;
  else emit('delete', variable.key);
};

const confirmDelete = key => {
  confirmingKey.value = null;
  emit('delete', key);
};
</script>

<template>
  <!-- Overlays the right edge instead of taking a column of its own: the canvas is already
       sharing the row with the block palette, and squeezing it further hid nodes behind it. -->
  <aside
    class="absolute inset-y-0 right-0 z-20 w-80 flex flex-col gap-3 rounded-l-lg border-l border-n-weak bg-n-solid-1 shadow-xl p-3 min-h-0"
  >
    <div class="flex items-start justify-between gap-2 flex-shrink-0">
      <div class="min-w-0">
        <h3 class="text-body-two-bold text-n-slate-12">
          {{ t('FLOWS.EDITOR.VARIABLES.TITLE') }}
        </h3>
        <p class="mt-0.5 text-xs text-n-slate-10">
          {{ t('FLOWS.EDITOR.VARIABLES.DESCRIPTION') }}
        </p>
      </div>
      <Button
        v-tooltip.bottom="t('FLOWS.EDITOR.VARIABLES.CLOSE')"
        icon="i-lucide-x"
        slate
        ghost
        xs
        class="flex-shrink-0"
        @click="emit('close')"
      />
    </div>

    <form
      class="flex items-center gap-2 flex-shrink-0"
      @submit.prevent="create"
    >
      <input
        v-model="search"
        type="text"
        :placeholder="t('FLOWS.EDITOR.VARIABLES.SEARCH_PLACEHOLDER')"
        class="reset-base flex-1 min-w-0 py-2 !px-3 text-sm rounded-lg outline outline-1 outline-n-weak focus:outline-n-brand bg-n-alpha-black2 text-n-slate-12"
      />
      <Button
        v-if="canCreate"
        v-tooltip.bottom="t('FLOWS.EDITOR.VARIABLES.CREATE_BUTTON')"
        type="submit"
        icon="i-lucide-plus"
        blue
        xs
        class="flex-shrink-0"
      />
    </form>

    <p v-if="createError" class="-mt-1 text-xs text-n-ruby-11 flex-shrink-0">
      {{ createError }}
    </p>

    <div class="flex flex-col gap-1 overflow-y-auto min-h-0">
      <div
        v-for="variable in visibleVariables"
        :key="variable.key"
        class="flex flex-col gap-1 px-1 py-1 rounded-md hover:bg-n-slate-2"
      >
        <div class="flex items-center gap-1">
          <input
            :value="displayValue(variable)"
            type="text"
            class="reset-base flex-1 min-w-0 !px-2 !py-1 text-sm font-mono rounded-md bg-transparent outline outline-1 outline-transparent hover:outline-n-weak focus:outline-n-brand focus:bg-n-alpha-black2 text-n-slate-12"
            @focus="startEdit(variable)"
            @input="editing && (editing.draft = $event.target.value)"
            @keyup.enter="$event.target.blur()"
            @keydown.esc="cancelEdit"
            @blur="commitEdit"
          />

          <Popover v-if="usageCount(variable)" align="end">
            <Button
              v-tooltip.bottom="t('FLOWS.EDITOR.VARIABLES.DETAILS')"
              icon="i-lucide-more-horizontal"
              slate
              faded
              xs
              class="flex-shrink-0"
            />
            <template #content>
              <div class="w-64 p-3 flex flex-col gap-3">
                <div
                  v-for="group in [
                    {
                      label: t('FLOWS.EDITOR.VARIABLES.WRITTEN_IN'),
                      nodes: variable.writtenIn,
                    },
                    {
                      label: t('FLOWS.EDITOR.VARIABLES.READ_IN'),
                      nodes: variable.readIn,
                    },
                  ].filter(g => g.nodes.length)"
                  :key="group.label"
                  class="flex flex-col gap-1"
                >
                  <span
                    class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
                  >
                    {{ group.label }}
                  </span>
                  <div
                    v-for="node in group.nodes"
                    :key="node.id"
                    class="flex flex-col min-w-0"
                  >
                    <span class="text-xs text-n-slate-12 truncate">
                      {{ node.name }}
                    </span>
                    <span class="text-[10px] text-n-slate-10 truncate">
                      {{ node.typeLabel }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </Popover>

          <Button
            v-tooltip.bottom="t('FLOWS.EDITOR.VARIABLES.DELETE')"
            icon="i-lucide-trash-2"
            ruby
            faded
            xs
            class="flex-shrink-0"
            @click="requestDelete(variable)"
          />
        </div>

        <p
          v-if="editing?.key === variable.key && editingError"
          class="px-2 text-xs text-n-ruby-11"
        >
          {{ editingError }}
        </p>

        <span
          v-else-if="usageCount(variable)"
          class="px-2 text-[10px] text-n-slate-10"
        >
          {{
            t('FLOWS.EDITOR.VARIABLES.USED_IN', { count: usageCount(variable) })
          }}
        </span>
        <span v-else class="px-2 text-[10px] text-n-slate-10">
          {{ t('FLOWS.EDITOR.VARIABLES.UNUSED') }}
        </span>

        <div
          v-if="confirmingKey === variable.key"
          class="flex flex-col gap-2 p-2 rounded-md bg-n-ruby-3"
        >
          <p class="text-xs text-n-ruby-11">
            {{
              t('FLOWS.EDITOR.VARIABLES.DELETE_CONFIRM', {
                count: usageCount(variable),
              })
            }}
          </p>
          <div class="flex items-center gap-2">
            <Button
              ruby
              xs
              :label="t('FLOWS.EDITOR.VARIABLES.CONFIRM_DELETE')"
              @click="confirmDelete(variable.key)"
            />
            <Button
              slate
              faded
              xs
              :label="t('FLOWS.EDITOR.VARIABLES.CANCEL')"
              @click="confirmingKey = null"
            />
          </div>
        </div>
      </div>

      <p
        v-if="!visibleVariables.length"
        class="px-2 py-6 text-sm text-center text-n-slate-10"
      >
        {{
          search.trim()
            ? t('FLOWS.EDITOR.VARIABLES.NO_RESULTS')
            : t('FLOWS.EDITOR.VARIABLES.EMPTY')
        }}
      </p>
    </div>
  </aside>
</template>
